import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_LANGUAGE, WORD_LENGTH, isValidGuess, loadInsightsFor, loadLength, type Language,
} from '@lexicon/core';

export interface LengthValidator {
  /**
   * The accepted-guess check for this length, or `null` while its chunk is
   * still loading. `null` is a *state*, not an error: callers must not
   * treat a guess as invalid while it holds.
   */
  isValid: ((word: string) => boolean) | null;
  /** Re-kick a load that failed or has not finished. Cheap and idempotent. */
  retry: () => void;
}

/**
 * The one list that ships in the main bundle: English at five letters.
 * Every other length — and every Dutch length, five included — is a chunk.
 */
function isBundled(length: number | null, language: Language): boolean {
  return length === WORD_LENGTH && language === DEFAULT_LANGUAGE;
}

/**
 * The word data a board of `length` needs in `language`, fetched once and
 * cached by core's `loadLength` (spec §10.5). Answers are already in the
 * main bundle — this is only ever waiting on the accepted-guess list.
 *
 * Pass `null` for "no board yet" so a screen that has not booted does not
 * pull down a chunk for a length it is about to change its mind about.
 */
export function useLengthValidator(
  length: number | null,
  language: Language = DEFAULT_LANGUAGE,
): LengthValidator {
  // Lazy initializer, not `useState(null)`: the bundled length must never
  // read as pending, not even for the one frame between the initial render
  // and the effect below actually running. A synchronous consumer that
  // reads `isValid` during that first render (e.g. a board mounted in the
  // same pass) would otherwise see `null` and toast "Checking word list…"
  // for a length that was never actually waiting on anything.
  const [isValid, setIsValid] = useState<((word: string) => boolean) | null>(
    () => (isBundled(length, language) ? isValidGuess : null),
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (length === null) return undefined;
    if (isBundled(length, language)) {
      // Bundled: never a pending state, not even for one frame.
      setIsValid(() => isValidGuess);
      return undefined;
    }
    let cancelled = false;
    setIsValid(null);
    void loadLength(length, language)
      // The extra arrow is not noise: setState treats a bare function as
      // an updater and would call it with the previous state.
      .then((data) => { if (!cancelled) setIsValid(() => data.isValid); })
      .catch(() => { /* stays null; the next Enter retries */ });
    // Insights are a nicety (spec §3.7) — a game must never wait on them.
    void loadInsightsFor(length, language).catch(() => {});
    return () => { cancelled = true; };
  }, [length, language, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { isValid, retry };
}
